import { useConfig, useI18n, useApi } from '~/common/context';
import {
  CrudOptions,
  OverridableQuery,
  serialize,
  useCrud,
  useQuery,
  UseQueryOptions,
} from '@wyz/utils';
import {
  ICountry,
  ICountryList,
  ICountryPayload,
  ICountryRecordByLetter,
  ICriteriaValueUsageError,
} from '~/connect/types';
import { ConnectQueryKeys } from '~/connect/api';
import { COUNTRY_STATUS } from '~/connect/schemas';
import { IErrorApiResponse, IValidationError } from '@wyz/types';
import { notify } from '@wyz/ui';
import { CommonErrorCodes } from '~/common/helpers';
import { useCommonErrorTranslation } from '~/common/hooks';
import { CommonErrorCodesEnumType } from '~/common/types';

type Actions = 'create' | 'getOneById' | 'remove';

type Options = Pick<CrudOptions<ICountry, ICountryPayload>, Actions> & {
  listActive?: OverridableQuery<
    UseQueryOptions<ICountryList, IErrorApiResponse>
  >;
  getAll?: OverridableQuery<
    UseQueryOptions<ICountryRecordByLetter, IErrorApiResponse>
  >;
};

const groupByLetter = (data: ICountryList['data']) => {
  return data.reduce((acc, curr) => {
    const letter = curr.name[0].toUpperCase();
    if (!acc[letter]) {
      acc[letter] = [];
    }
    acc[letter].push(curr);
    return acc;
  }, {} as ICountryRecordByLetter);
};

export const useManageCountries = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const { t } = useI18n();
  const { resolveErrorKey } = useCommonErrorTranslation();
  const api = useCrud<
    Actions,
    ICountry,
    ICountryPayload,
    ICountryPayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType,
      ICriteriaValueUsageError
    >
  >(
    {
      baseKey: ConnectQueryKeys.countries.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/countries`,
    },
    {
      ...options,
      create: {
        ...options.create,
        keysToInvalidateOnSuccess: () => [
          ConnectQueryKeys.countries.base(),
          { key: ConnectQueryKeys.countries.active(), type: 'all' },
        ],
      },
      remove: {
        ...options.remove,
        keysToInvalidateOnSuccess: () => [
          ConnectQueryKeys.countries.base(),
          { key: ConnectQueryKeys.countries.active(), type: 'all' },
        ],
      },
    },
  );

  const getActiveCountries = useQuery({
    queryKey: ConnectQueryKeys.countries.active(),
    queryFn: () =>
      callCoreApi<ICountryList>(
        `/tenants/api/wtb/tenants/${tenantCode}/countries?${serialize({
          criteria: {
            operator: 'and',
            conditions: [
              {
                column: 'status',
                operator: 'in',
                value: [COUNTRY_STATUS.ACTIVE],
              },
            ],
          },
        })}`,
        {},
      ),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    ...options.listActive,
    enabled: Boolean(options.listActive?.enabled),
  });
  const getAll = useQuery({
    queryKey: ConnectQueryKeys.countries.base(),
    queryFn: () =>
      callCoreApi<ICountryList>(
        `/tenants/api/wtb/tenants/${tenantCode}/countries`,
      ).then((data) => {
        return groupByLetter(data.data);
      }),
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    ...options.getAll,
    enabled: Boolean(options.getAll?.enabled),
  });

  const toggle = (countryPayload: ICountryPayload, activate: boolean) => {
    if (activate) {
      api.create.mutate(countryPayload, {
        onSuccess: () => {
          notify.success(t('app.country.notifications.activated'), {
            id: 'toast-success-country-activated',
          });
        },
        onError: (errorResponse) => {
          if (errorResponse.errors) {
            errorResponse.errors.map((error) =>
              notify.error(t(resolveErrorKey(error.errors[0])), {
                id: 'toast-error-country-activation-validation',
              }),
            );
          } else {
            notify.error(t('app.country.notifications.activation_failed'), {
              id: 'toast-error-country-activation-failed',
            });
          }
        },
      });
    } else {
      api.remove.mutate(String(countryPayload.id), {
        onSuccess: () => {
          notify.success(t('app.country.notifications.inactivated'), {
            id: 'toast-success-country-deactivated',
          });
        },
        onError: (error) => {
          if (error.message === CommonErrorCodes.ERR_CRITERIA_VALUES_IN_USE) {
            notify.error(
              t(resolveErrorKey(error.message), {
                countCustomer: error.meta.CUSTOMER,
                countSupplierTemplate: error.meta.SUPPLIER_TEMPLATE,
                countBrandTemplate: error.meta.BRAND_TEMPLATE,
                countAdvert: error.meta.ADVERT,
                countTheme: error.meta.THEME,
                countSettingTemplate: error.meta.SETTING_TEMPLATE,
                countQuoteBrandTemplate: error.meta.QUOTE_BRAND_TEMPLATE,
                countRedistributionBrandTemplate:
                  error.meta.REDISTRIBUTION_BRAND_TEMPLATE,
                countRedistributionSupplierTemplate:
                  error.meta.REDISTRIBUTION_SUPPLIER_TEMPLATE,
                countWheelSupplierTemplate: error.meta.WHEEL_SUPPLIER_TEMPLATE,
                countS2sFlowTemplate: error.meta.S2S_FLOW_TEMPLATE,
              }),
              { id: 'toast-error-country-deactivation-in-use' },
            );
          } else {
            notify.error(t(resolveErrorKey(error.message)), {
              id: 'toast-error-country-deactivation-failed',
            });
          }
        },
      });
    }
  };

  return {
    ...api,
    toggle,
    getAll,
    listActive: getActiveCountries,
  };
};
